import {
  CloudUploadOutlined,
  DescriptionOutlined,
  DownloadOutlined,
  EmailOutlined,
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  useLazyDownloadSignedConsentLetterQuery,
  useUploadSignedConsentLetterMutation,
} from "api/api-post-cc";
import React from "react";
import { toast } from "react-toastify";

type ConsentLetterToDoProps = {
  accountRelationshipId: string;
  partnerName?: string;
  template: any;
  signedDocument?: any;
};

const getTemplateCandidates = (template: any) => [
  template,
  template?.data,
  template?.template,
  template?.consent_letter,
  template?.document,
  template?.email_template,
  template?.documents?.[0],
  template?.data?.document,
  template?.data?.documents?.[0],
];

const getTemplateUrl = (template: any) =>
  getTemplateCandidates(template)
    .map(
      (candidate) =>
        candidate?.download_url ??
        candidate?.file_url ??
        candidate?.template_url ??
        candidate?.url ??
        candidate?.document_url,
    )
    .find((value): value is string => typeof value === "string" && Boolean(value));

const getTemplateContent = (template: any) =>
  getTemplateCandidates(template)
    .flatMap((candidate) => [
      candidate,
      candidate?.content,
      candidate?.html,
      candidate?.body,
      candidate?.template_content,
    ])
    .find(
      (value): value is string => typeof value === "string" && Boolean(value),
    );

const getTemplateName = (template: any) =>
  getTemplateCandidates(template)
    .map((candidate) => candidate?.file_name ?? candidate?.name)
    .find((value): value is string => typeof value === "string" && Boolean(value));

const ConsentLetterToDo = ({
  accountRelationshipId,
  partnerName,
  template,
  signedDocument,
}: ConsentLetterToDoProps) => {
  const [isUploadOpen, setIsUploadOpen] = React.useState(false);
  const [file, setFile] = React.useState<File>();
  const [isTemplateDownloadRequested, setIsTemplateDownloadRequested] =
    React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [
    downloadConsentLetterTemplate,
    {
      data: downloadedConsentLetterTemplate,
      isFetching: isDownloadingTemplate,
      isError: isTemplateDownloadError,
    },
  ] = useLazyDownloadSignedConsentLetterQuery();
  const [uploadSignedConsentLetter, { isLoading: isUploading }] =
    useUploadSignedConsentLetterMutation();

  const handleTemplateDownload = () => {
    setIsTemplateDownloadRequested(true);
    downloadConsentLetterTemplate({
      account_relationship_id: accountRelationshipId,
    });
  };

  React.useEffect(() => {
    if (!isTemplateDownloadRequested || !downloadedConsentLetterTemplate) {
      return;
    }

    const downloadUrl = getTemplateUrl(downloadedConsentLetterTemplate);
    const downloadFileName =
      getTemplateName(downloadedConsentLetterTemplate) ||
      getTemplateName(template) ||
      "consent-letter-template.pdf";

    if (!downloadUrl) {
      toast.error("Consent letter template is not available for download");
      setIsTemplateDownloadRequested(false);
      return;
    }

    // Download from the signed URL so PDFs and images are saved as files,
    // rather than displaying the API response object in the browser.
    fetch(downloadUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to download the consent letter template");
        }
        return response.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        // The direct link is a fallback when the file host does not allow fetch.
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = downloadFileName;
        link.setAttribute("download", downloadFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      });

    setIsTemplateDownloadRequested(false);
  }, [
    downloadedConsentLetterTemplate,
    isTemplateDownloadRequested,
    template,
  ]);

  React.useEffect(() => {
    if (isTemplateDownloadRequested && isTemplateDownloadError) {
      toast.error("Failed to download the consent letter template");
      setIsTemplateDownloadRequested(false);
    }
  }, [isTemplateDownloadError, isTemplateDownloadRequested]);

  const handleDownload = (document: any, fallbackName: string) => {
    const templateUrl = getTemplateUrl(document);

    if (templateUrl) {
      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = getTemplateName(document) || fallbackName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    const content = getTemplateContent(document);
    if (!content) {
      toast.error("Consent letter template is not available for download");
      return;
    }

    const blob = new Blob([content], { type: "text/html" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = getTemplateName(document) || fallbackName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const handleClose = () => {
    if (isUploading) return;
    setFile(undefined);
    setIsUploadOpen(false);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Select the signed consent letter to upload");
      return;
    }

    try {
      await uploadSignedConsentLetter({
        account_relationship_id: accountRelationshipId,
        file,
      }).unwrap();
      toast.success("Signed consent letter uploaded successfully");
      handleClose();
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          error?.message ||
          "Failed to upload the signed consent letter",
      );
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          my: 1,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 2,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <EmailOutlined color="primary" />
            <Box>
              <Typography fontWeight={700}>Consent Letter</Typography>
              <Typography variant="caption" color="text.secondary">
                {partnerName
                  ? `Template available for ${partnerName}`
                  : "Download the template and upload the signed copy"}
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ p: 2 }}
        >
          {template && (
            <Button
              variant="outlined"
              startIcon={<DownloadOutlined />}
              onClick={handleTemplateDownload}
              disabled={isDownloadingTemplate}
              sx={{ textTransform: "none" }}
            >
              {isDownloadingTemplate ? "Downloading..." : "Download Template"}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<CloudUploadOutlined />}
            onClick={() => setIsUploadOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Upload Signed Template
          </Button>
        </Stack>
        {signedDocument && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{
              px: 2,
              py: 1.25,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "rgba(22, 163, 74, 0.04)",
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <DescriptionOutlined color="success" fontSize="small" />
              <Typography variant="body2" fontWeight={600}>
                Signed consent letter available
              </Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<DownloadOutlined />}
              onClick={() =>
                handleDownload(signedDocument, "signed-consent-letter.pdf")
              }
              sx={{ textTransform: "none" }}
            >
              Download Signed Letter
            </Button>
          </Stack>
        )}
      </Paper>

      <Dialog open={isUploadOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Upload Signed Consent Letter</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload the signed consent letter for this account relationship.
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(event) => setFile(event.target.files?.[0])}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<CloudUploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              sx={{ textTransform: "none" }}
            >
              Select File
            </Button>
            <Typography variant="body2" color="text.secondary">
              {file?.name || "No file selected"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleUpload}
            loading={isUploading}
            disabled={!file}
          >
            Upload
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConsentLetterToDo;
